/**
 * Pure scroll policy for ChatView timeline follow/anchor effects.
 * Keeps animated scrollToIndex from competing with continuous reveal/follow commands.
 */

export type TimelineScrollMode = "following-end" | "anchoring-new-turn" | "free-scrolling";

export type TimelineScrollCommand =
  | { readonly type: "none" }
  | { readonly type: "scrollToOffset"; readonly animated: false }
  | { readonly type: "scrollToEnd"; readonly animated: false };

export type TimelineScrollCommandLogEntry =
  | {
      readonly type: "scrollToIndex";
      readonly index: number;
      readonly animated: boolean;
    }
  | {
      readonly type: "scrollToOffset";
      readonly offset: number;
      readonly animated: boolean;
    }
  | {
      readonly type: "scrollToEnd";
      readonly animated: boolean;
    };

export function createTimelineScrollCommandLog() {
  const entries: TimelineScrollCommandLogEntry[] = [];
  return {
    entries,
    record(entry: TimelineScrollCommandLogEntry) {
      entries.push(entry);
    },
    clear() {
      entries.length = 0;
    },
  };
}

/**
 * While an animated scrollToIndex is in flight, freeze reveal/follow offsets.
 * After settle, anchoring-new-turn may reveal the turn end; following-end may stick.
 */
export function decideTimelineFollowScroll(input: {
  readonly mode: TimelineScrollMode;
  readonly anchorScrollInFlight: boolean;
  readonly scrollDeltaToRevealEnd: number;
  readonly realContentOverflowsViewport: boolean;
}): TimelineScrollCommand {
  if (input.mode === "free-scrolling") {
    return { type: "none" };
  }
  if (input.anchorScrollInFlight) {
    return { type: "none" };
  }
  if (input.mode === "anchoring-new-turn") {
    if (input.scrollDeltaToRevealEnd <= 1) {
      return { type: "none" };
    }
    return { type: "scrollToOffset", animated: false };
  }
  if (input.mode !== "following-end") {
    return { type: "none" };
  }
  if (!input.realContentOverflowsViewport) {
    return { type: "none" };
  }
  return { type: "scrollToEnd", animated: false };
}

/** Assert helper for the jagged-scroll feedback loop. */
export function animatedScrollToIndexHasCompetingCommands(
  log: ReadonlyArray<TimelineScrollCommandLogEntry>,
): boolean {
  let animatedInFlight = false;
  for (const entry of log) {
    if (entry.type === "scrollToIndex" && entry.animated) {
      animatedInFlight = true;
      continue;
    }
    if (!animatedInFlight) {
      continue;
    }
    // Settle marker: non-animated scrollToOffset that pins the current offset
    // after scrollend is allowed; competing reveal/follow is not.
    if (entry.type === "scrollToOffset" || entry.type === "scrollToEnd") {
      if (entry.type === "scrollToEnd") return true;
      if (entry.animated) return true;
      // Non-animated offset while in flight is competing unless it's the settle pin.
      // Treat any offset after animated index (before a later non-animated index settle
      // sequence ends) as competing when another animated index hasn't finished.
      return true;
    }
  }
  return false;
}
